import { differenceInCalendarWeeks, isValid, parseISO, startOfWeek } from 'date-fns'
import type {
  TrainingPlan,
  TrainingPlanDiscipline,
  TrainingPlanPayload,
  TrainingPlanStatus,
  TrainingPlanWeekKind,
  TrainingExperience,
  WeekDay,
} from '../../types/training-plan'

/**
 * Editable shape of the questionnaire, decoupled from the API payload. The
 * athlete is always the current user; a coach (when invited) is passed
 * separately to the create call, not collected in the form.
 */
export interface PlanFormData {
  start_date: string
  discipline: TrainingPlanDiscipline
  training_experience: TrainingExperience
  length_weeks: number
  session_duration_minutes: number | ''
  sessions_per_week: number | ''
  preferred_session_days: WeekDay[]
  goal: string
  typical_grade: string
  best_grade: string
  style_strengths: string
  style_limiters: string
  recent_training: string
  facilities: string
  work_demands: string
  other_activity: string
  injuries_constraints: string
  preferences: string
}

export function emptyPlanForm(): PlanFormData {
  return {
    start_date: '',
    discipline: 'bouldering',
    training_experience: 'intermediate',
    length_weeks: 4,
    session_duration_minutes: 90,
    sessions_per_week: 3,
    preferred_session_days: [],
    goal: '',
    typical_grade: '',
    best_grade: '',
    style_strengths: '',
    style_limiters: '',
    recent_training: '',
    facilities: '',
    work_demands: '',
    other_activity: '',
    injuries_constraints: '',
    preferences: '',
  }
}

export function planToForm(plan: TrainingPlan): PlanFormData {
  return {
    start_date: plan.start_date ?? '',
    discipline: plan.discipline ?? 'bouldering',
    training_experience: plan.training_experience ?? 'intermediate',
    length_weeks: plan.length_weeks ?? 4,
    session_duration_minutes: plan.session_duration_minutes ?? '',
    sessions_per_week: plan.sessions_per_week ?? '',
    preferred_session_days: plan.preferred_session_days ?? [],
    goal: plan.goal ?? '',
    typical_grade: plan.typical_grade ?? '',
    best_grade: plan.best_grade ?? '',
    style_strengths: plan.style_strengths ?? '',
    style_limiters: plan.style_limiters ?? '',
    recent_training: plan.recent_training ?? '',
    facilities: plan.facilities ?? '',
    work_demands: plan.work_demands ?? '',
    other_activity: plan.other_activity ?? '',
    injuries_constraints: plan.injuries_constraints ?? '',
    preferences: plan.preferences ?? '',
  }
}

function blankToNull(value: string): string | null {
  return value.trim() === '' ? null : value
}

/** Maps the questionnaire form onto the API attributes (plan fields only). */
export function formToPayload(form: PlanFormData): TrainingPlanPayload {
  return {
    start_date: blankToNull(form.start_date),
    discipline: form.discipline,
    training_experience: form.training_experience,
    length_weeks: form.length_weeks,
    session_duration_minutes:
      form.session_duration_minutes === '' ? null : Number(form.session_duration_minutes),
    sessions_per_week: form.sessions_per_week === '' ? null : Number(form.sessions_per_week),
    preferred_session_days: form.preferred_session_days,
    goal: blankToNull(form.goal),
    typical_grade: blankToNull(form.typical_grade),
    best_grade: blankToNull(form.best_grade),
    style_strengths: blankToNull(form.style_strengths),
    style_limiters: blankToNull(form.style_limiters),
    recent_training: blankToNull(form.recent_training),
    facilities: blankToNull(form.facilities),
    work_demands: blankToNull(form.work_demands),
    other_activity: blankToNull(form.other_activity),
    injuries_constraints: blankToNull(form.injuries_constraints),
    preferences: blankToNull(form.preferences),
  }
}

/**
 * 1-based plan week the given date falls in, or null when the date sits outside
 * the plan (or the plan has no schedule yet). Weeks run Monday to Sunday, like
 * everywhere else in the diary.
 */
export function planWeekFor(plan: TrainingPlan, date: Date): number | null {
  if (!plan.start_date || !plan.length_weeks) return null

  const start = parseISO(plan.start_date)
  if (!isValid(start)) return null

  const weekNumber =
    differenceInCalendarWeeks(date, startOfWeek(start, { weekStartsOn: 1 }), { weekStartsOn: 1 }) + 1

  return weekNumber >= 1 && weekNumber <= plan.length_weeks ? weekNumber : null
}

/** Colour for a week-kind label: rest is sky/azure, base is orange. */
export function weekKindBadgeClass(kind: TrainingPlanWeekKind): string {
  return kind === 'rest'
    ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
    : 'bg-orange-500/15 text-orange-600 dark:text-orange-400'
}

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline'

export function statusBadgeVariant(status: TrainingPlanStatus): BadgeVariant {
  switch (status) {
    case 'active':
      return 'default'
    case 'pending':
    case 'completed':
      return 'secondary'
    case 'rejected':
      return 'destructive'
    case 'draft':
    default:
      return 'outline'
  }
}
